import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Text,
  Button,
  HStack,
  VStack,
  Checkbox,
  Link,
  Dialog,
  Portal,
  CloseButton,
} from '@chakra-ui/react'
import GeneticConsentContent from './GeneticConsentContent'
import { CURRENT_GENETIC_CONSENT_VERSION } from '../../config/legalVersions'

interface GeneticConsentBannerProps {
  isVisible: boolean
  onAccept?: () => void | Promise<void>
}

/**
 * Consent to genetic data use.
 *
 * Kept separate from the Terms/Privacy banner on purpose: the state genetic
 * privacy statutes require consent that is specific and separately given, so
 * this has its own unticked checkbox and its own acceptance call. Accepting the
 * Terms or the Privacy Policy does not accept this.
 */
export const GeneticConsentBanner: React.FC<GeneticConsentBannerProps> = ({
  isVisible,
  onAccept,
}) => {
  const [checked, setChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const bannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = bannerRef.current
    if (!isVisible || !el) {
      document.documentElement.style.removeProperty('--lw-bottom-banner-height')
      return
    }
    const apply = () => {
      document.documentElement.style.setProperty(
        '--lw-bottom-banner-height',
        `${Math.ceil(el.getBoundingClientRect().height)}px`,
      )
    }
    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(el)
    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty('--lw-bottom-banner-height')
    }
  }, [isVisible])

  if (!isVisible) {
    return null
  }

  const handleAccept = async () => {
    if (!checked || submitting) return
    setSubmitting(true)
    try {
      await onAccept?.()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Box
        ref={bannerRef}
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex="banner"
        bg="white"
        borderTopWidth="3px"
        borderColor="green.600"
        boxShadow="0 -8px 24px rgba(0,0,0,0.12)"
        maxH="calc(100dvh - 4rem)"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <VStack align="stretch" gap={3} maxW="6xl" mx="auto" w="full" p={4} pb={2} overflowY="auto">
          <VStack align="start" gap={1}>
            <Text fontWeight="semibold" fontSize="md" color="green.800">
              Check this box to keep your genetic results available
            </Text>
            <Text fontSize="sm" color="gray.700">
              This consent is separate from the Privacy Policy, so you can withdraw it later
              without closing your account. We never sell your data.
            </Text>
          </VStack>
        </VStack>

        <Box
          borderTopWidth="1px"
          borderColor="green.200"
          bg="green.50"
          px={4}
          pt={3}
          pb={{ base: "calc(0.75rem + env(safe-area-inset-bottom))", md: 3 }}
        >
          <VStack align="stretch" gap={3} maxW="6xl" mx="auto">
            <Box
              bg="white"
              borderWidth="2px"
              borderColor="green.700"
              borderRadius="md"
              p={3}
            >
              <Checkbox.Root
                checked={checked}
                onCheckedChange={(e) => setChecked(e.checked === true)}
                colorPalette="green"
                size="lg"
                variant="solid"
                alignItems="start"
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control
                  mt={1}
                  flexShrink={0}
                  borderWidth="2px"
                  borderColor="green.800"
                  bg="white"
                >
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label cursor="pointer" lineHeight="tall" fontWeight="500" color="gray.800">
                  <Text as="span" fontWeight="bold" color="green.800">Required. </Text>
                  I consent to Livewello storing, analyzing, and processing my genetic data as
                  described in the{' '}
                  <Link
                    href="/genetic-consent"
                    target="_blank"
                    rel="noopener noreferrer"
                    color="green.700"
                    textDecoration="underline"
                  >
                    Consent to Use of Your Genetic Data
                  </Link>{' '}
                  (v{CURRENT_GENETIC_CONSENT_VERSION}).
                </Checkbox.Label>
              </Checkbox.Root>
            </Box>

            <HStack gap={3} justify="flex-end" wrap="wrap">
              {!checked && (
                <Text fontSize="sm" color="green.800" fontWeight="medium" mr="auto">
                  Check the box to turn on Give consent.
                </Text>
              )}
              <Button size="md" variant="outline" onClick={() => setReviewOpen(true)}>
                Review
              </Button>
              <Button
                size="md"
                colorPalette="green"
                onClick={handleAccept}
                disabled={!checked || submitting}
                loading={submitting}
              >
                Give consent
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Box>

      <Dialog.Root
        open={reviewOpen}
        onOpenChange={(e) => setReviewOpen(e.open)}
        size="lg"
        scrollBehavior="inside"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Consent to Use of Your Genetic Data</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                <GeneticConsentContent compact />
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="outline" onClick={() => setReviewOpen(false)}>
                  Close
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}

export default GeneticConsentBanner
